// backlog-review-import.ts — Logique PURE du bilan d'un réimport de kit.
//
// Le serveur rend quatre nombres et une liste de motifs ; l'écran doit en faire
// une phrase juste. Deux règles, et ce sont celles qu'une régression casserait
// sans qu'aucun test d'écran ne s'en aperçoive :
//
// 1. **Un import qui n'a rien chargé n'est pas une réussite.** Le fichier est
//    accepté, le contrat est respecté, et pourtant rien n'entre : le ton doit
//    le dire, sinon l'utilisateur repart en croyant sa revue enrichie.
// 2. **On annonce ce qui est ENTRÉ, pas ce qui a été lu.** Les lignes refusées
//    se comptent à part, et elles se listent — un nombre seul n'apprend pas
//    quoi corriger.

export interface ImportReason {
  line: number;
  reason: string;
}

export interface ImportResult {
  loaded: number;
  rejected: number;
  created: number;
  updated: number;
  reasons: ImportReason[];
  warnings: string[];
}

export type ImportTone = 'success' | 'warning' | 'error';

/** Une clé de libellé et ses paramètres. Le module d'écran appelle `t`. */
export interface SummaryLine {
  key: string;
  params: Record<string, string>;
}

/** Le ton du bilan. Trois cas — et le troisième n'est pas une réussite. */
export function importTone(result: ImportResult): ImportTone {
  if (result.loaded === 0) return 'error';
  return result.rejected > 0 ? 'warning' : 'success';
}

/** Le bilan en phrases. Accord au singulier compris : « 1 recommandation ». */
export function importSummaryLines(result: ImportResult): SummaryLine[] {
  const lines: SummaryLine[] = [];
  if (result.loaded === 0) lines.push({ key: 'review.import.loadedNone', params: {} });
  else if (result.loaded === 1) lines.push({ key: 'review.import.loadedOne', params: {} });
  else lines.push({ key: 'review.import.loadedMany', params: { n: String(result.loaded) } });

  // Le détail nouvelles / rafraîchies dit l'invariant de l'écran : une passe
  // FUSIONNE, elle ne recommence pas. Sans lui, un import qui rafraîchit
  // quarante recommandations déjà triées se lit comme s'il en créait quarante.
  if (result.loaded > 0) {
    lines.push({
      key: 'review.import.split',
      params: { created: String(result.created), updated: String(result.updated) },
    });
  }

  if (result.rejected === 1) lines.push({ key: 'review.import.rejectedOne', params: {} });
  else if (result.rejected > 1)
    lines.push({ key: 'review.import.rejectedMany', params: { n: String(result.rejected) } });

  return lines;
}
